import { motion } from 'framer-motion';
import { ClipboardList, BarChart2, CheckSquare, ShieldCheck, HardHat, Calendar, Layers, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { fadeUp, staggerContainer, defaultViewport } from '../components/ui/AnimationVariants';
import OperationalSection from '../components/OperationalSection';

const operationalSections = [
  {
    icon: ClipboardList,
    title: 'Preconstruction Planning',
    body: 'We engage during preconstruction to review drawings, identify scope gaps, coordinate with the design team on electrical load requirements, and provide accurate budget input before the project breaks ground. Early involvement prevents field conflicts and schedule delays.',
  },
  {
    icon: BarChart2,
    title: 'Project Management',
    body: 'Each project has a dedicated project manager who owns the schedule, procurement, subcontractor coordination, and owner communication. We track open RFIs, submittals, and change orders in real time and report status directly to the GC superintendent.',
  },
  {
    icon: CheckSquare,
    title: 'QA/QC Process',
    body: 'Inspections are built into every phase — rough-in, above-ceiling, and final trim. Our foremen sign off on each checkpoint before the next phase begins. We document deficiencies internally before they appear on a GC punch list.',
  },
  {
    icon: ShieldCheck,
    title: 'Safety Coordination',
    body: 'BPR Electric maintains an active safety program with site-specific plans, daily toolbox talks, and OSHA-compliant documentation on every project. Our EMR reflects a consistent safety record across commercial and multi-family work.',
  },
  {
    icon: HardHat,
    title: 'Field Supervision',
    body: 'We deploy licensed foremen with direct authority over their crews. Foremen attend GC coordination meetings, manage daily manpower allocation, and escalate issues to the project manager before they affect the schedule.',
  },
  {
    icon: Calendar,
    title: 'Scheduling & Coordination',
    body: 'We build and maintain a three-week lookahead schedule aligned with the master project schedule. Manpower is allocated in advance, not reactively. We flag conflicts — material lead times, inspection holds, design changes — before they become delays.',
  },
  {
    icon: Layers,
    title: 'Design-Build Collaboration',
    body: 'On design-build projects, we work directly with architects and MEP engineers from schematic design through construction documents. We provide constructability input, value-engineering options, and final-scope buyout packages that match the project budget.',
  },
];

const Capabilities = () => (
  <div>
    {/* Hero */}
    <motion.section
      variants={staggerContainer}
      initial="hidden"
      animate="visible"
      className="section-standard bg-navy-950"
    >
      <div className="max-w-7xl mx-auto px-6">
        <motion.p variants={fadeUp} className="eyebrow">Capabilities</motion.p>
        <motion.h1 variants={fadeUp} className="font-heading text-h1 text-white mt-4 max-w-3xl">
          Capabilities
        </motion.h1>
        <motion.p variants={fadeUp} className="text-slate-400 text-lg leading-relaxed mt-6 max-w-2xl">
          How BPR Electric is organized to execute commercial electrical work — from preconstruction
          through final inspection.
        </motion.p>
      </div>
    </motion.section>

    {/* Operational Sections Grid */}
    <motion.section
      variants={staggerContainer}
      initial="hidden"
      whileInView="visible"
      viewport={defaultViewport}
      className="section-standard bg-navy-900"
    >
      <div className="max-w-7xl mx-auto px-6">
        <motion.p variants={fadeUp} className="eyebrow">How We Work</motion.p>
        <motion.h2 variants={fadeUp} className="font-heading text-h2 text-white mt-4">
          How We Work
        </motion.h2>
        <motion.p variants={fadeUp} className="text-slate-400 text-lg leading-relaxed mt-6 max-w-2xl">
          Commercial developers and GCs need an electrical sub that is organized, communicates clearly,
          and does not create problems on the schedule. The following sections describe how BPR Electric
          is structured to meet that standard.
        </motion.p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-navy-600 mt-16">
          {operationalSections.map((section, index) => (
            <OperationalSection key={index} {...section} />
          ))}
        </div>
      </div>
    </motion.section>

    {/* Markets Served */}
    <motion.section
      variants={staggerContainer}
      initial="hidden"
      whileInView="visible"
      viewport={defaultViewport}
      className="section-standard bg-navy-950"
    >
      <div className="max-w-7xl mx-auto px-6">
        <motion.p variants={fadeUp} className="eyebrow">Markets Served</motion.p>
        <motion.h2 variants={fadeUp} className="font-heading text-h2 text-white mt-4">
          Markets Served
        </motion.h2>
        <motion.p variants={fadeUp} className="text-slate-400 text-lg leading-relaxed mt-6 max-w-2xl">
          BPR Electric works across project types in the Massachusetts market. Our field experience
          covers the following sectors:
        </motion.p>
        <motion.div
          variants={staggerContainer}
          className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-px bg-navy-600 mt-12"
        >
          {[
            'Mixed-Use Commercial',
            'Class-A Office',
            'Multi-Family Residential',
            'Luxury Residential',
            'Retail & Tenant Fit-Out',
            'Industrial & Flex',
            'Solar & EV Infrastructure',
            'Neighborhood Infrastructure',
          ].map((market, i) => (
            <motion.div
              key={i}
              variants={fadeUp}
              className="bg-navy-950 px-6 py-4 text-slate-400 font-heading uppercase text-sm tracking-wide"
            >
              {market}
            </motion.div>
          ))}
        </motion.div>
      </div>
    </motion.section>

    {/* CTA */}
    <motion.section
      variants={staggerContainer}
      initial="hidden"
      whileInView="visible"
      viewport={defaultViewport}
      className="section-standard bg-navy-900"
    >
      <div className="max-w-7xl mx-auto px-6">
        <motion.p variants={fadeUp} className="eyebrow">Get Started</motion.p>
        <motion.h2 variants={fadeUp} className="font-heading text-h2 text-white mt-4 max-w-3xl">
          Evaluating Electrical Subs for an Upcoming Project?
        </motion.h2>
        <motion.p variants={fadeUp} className="text-slate-400 text-lg leading-relaxed mt-6 max-w-2xl">
          We're available for preconstruction conversations. Share the project scope and
          we'll respond with a direct assessment of fit, timeline, and approach.
        </motion.p>
        <motion.div variants={fadeUp} className="mt-10">
          <Link to="/contact" className="btn-primary inline-flex items-center">
            Start a Preconstruction Conversation
            <ArrowRight className="ml-2 h-5 w-5" />
          </Link>
        </motion.div>
      </div>
    </motion.section>
  </div>
);

export default Capabilities;
